/* eslint-disable react/no-unstable-nested-components */
import { UserRole } from "constants/userRole";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import {
  compareDateOnlyStringsDesc,
  formatDateOnlyForDisplay,
  localCalendarDateToIsoDateString,
  normalizeCalendarDayToLocalNoon,
  normalizeToIsoDateOnly,
} from "helpers/date";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { Tag } from "primereact/tag";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  useCopyChargeBasedPricingToContractMutation,
  useGetAllChargeBasedPricingQuery,
} from "store/queries/chargeBasedPricing";
import {
  useGetContractFeeScheduleQuery,
  useGetContractFeeSchedulesQuery,
  useCreateNextContractTermMutation,
  useDeleteContractTermMutation,
  IContractTermInGroup,
  RateCalculation,
  RateType,
  normalizeRateType,
  useExportFeeScheduleMutation,
  useGenerateProviderProceduresMutation,
  useUpdateContractFeeScheduleMutation,
  useActivateContractFeeScheduleMutation,
  resolveRateCalculationBaseRateDisplayName,
} from "store/queries/contractFeeSchedules";
import {
  useCopyExplodingFeeScheduleToContractMutation,
  useGetAllExplodingFeeSchedulesQuery,
} from "store/queries/explodingFeeSchedule";
import {
  useCopyInpatientRulesetToContractMutation,
  useGetInpatientRulesetsQuery,
} from "store/queries/inpatientRulesets";
import {
  useCopyLesserOfChargesToContractMutation,
  useGetAllLesserOfChargesQuery,
} from "store/queries/lesserOfCharges";
import {
  useCopyModifierAdjustmentToContractMutation,
  useGetAllModifierAdjustmentsQuery,
} from "store/queries/modifierAdjustments";
import {
  useCopyPackageRulesetToContractMutation,
  useGetAllPackageRulesetsQuery,
} from "store/queries/packageRuleset";
import {
  useCopyCaseRateToContractMutation,
  useGetCaseRateRulesetsQuery,
} from "store/queries/pricing";
import {
  useCopyDiscountRulesetToContractMutation,
  useGetAllDiscountRulesetsQuery,
} from "store/queries/procedureDiscountRuleset";
import {
  useCopyUnitCapToContractMutation,
  useGetAllUnitCapQuery,
} from "store/queries/unitCap";
import { authSelector } from "store/slices/auth";
import { SpecialtyList } from "types/Provider/Provider";

import AdjustmentRules from "./components/AdjustmentRules";
import ContractHistory from "./components/ContractHistory";
import { formatUpdateTerm } from "./helpers";
import { contractCanLinkPayers, linkedPayerCopy } from "./linkedPayers";
import { LINKED_PROVIDERS_COPY, sharedProviderNames } from "./linkedProviders";

export function ContractViewPage() {
  const { id, contractId } = useParams<{ id: string; contractId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // In hospital context, use contractId; in admin context, use id
  const actualContractId = contractId || id;
  const hospitalId = contractId ? id : null; // If contractId exists, then id is the hospital ID
  // CLE-2264 — rule management is gated by ROLE, not route: a super admin browsing a
  // contract via the hospital page gets the same Add rules / Copy rules from / status /
  // delete controls as on /payer-rules. Only true hospital-staff viewers are read-only.
  const { userInfo } = useUserWithAccount();
  const isSuperAdmin = userInfo?.scope === UserRole.SUPER_ADMIN;
  // The Adjustment Rules card treats a truthy hospitalId purely as a read-only flag.
  const rulesReadOnlyHospitalId = isSuperAdmin ? undefined : hospitalId;
  useDocumentTitle("Contract Details");
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { token } = useSelector(authSelector);

  const {
    data: contractData,
    isLoading,
    error,
    refetch,
  } = useGetContractFeeScheduleQuery(actualContractId!);
  const { data: baseRateFiles } = useGetAllBaseRateFilesQuery({
    accountId: hospitalId ?? undefined,
  });
  const [exportFeeSchedule] = useExportFeeScheduleMutation();
  const [generateProcedures] = useGenerateProviderProceduresMutation();
  const [updateContract] = useUpdateContractFeeScheduleMutation();
  const [activateContract] = useActivateContractFeeScheduleMutation();
  const [createNextTerm, { isLoading: isCreatingTerm }] =
    useCreateNextContractTermMutation();
  const [deleteContractTerm, { isLoading: isDeletingTerm }] =
    useDeleteContractTermMutation();

  const contract = contractData?.data;

  const passedTerms = (
    location.state as { termsInGroup?: IContractTermInGroup[] } | null
  )?.termsInGroup;

  const { data: contractList = [], refetch: refetchContractList } =
    useGetContractFeeSchedulesQuery(
      {
        providerId: contract?.providerId,
        payerId: contract?.payerId,
      },
      {
        skip: !contract?.providerId || contract?.payerId == null,
      }
    );

  const termsFromList = useMemo(() => {
    if (!contractList.length || !actualContractId) return undefined;
    const row = contractList.find((r) => {
      if (r.id === actualContractId) return true;
      // eslint-disable-next-line no-underscore-dangle
      return !!r._termsInGroup?.some((t) => t.id === actualContractId);
    });
    // eslint-disable-next-line no-underscore-dangle
    return row?._termsInGroup;
  }, [contractList, actualContractId]);

  /** Prefer API list so new terms appear after create; use navigation state only until list loads. */
  const termsInGroup = useMemo(() => {
    if (termsFromList && termsFromList.length > 0) return termsFromList;
    if (passedTerms && passedTerms.length > 0) return passedTerms;
    return termsFromList;
  }, [termsFromList, passedTerms]);

  const termSwitcherOptions = useMemo(() => {
    if (!termsInGroup?.length) return [];
    return [...termsInGroup]
      .sort((a, b) =>
        compareDateOnlyStringsDesc(a.effectiveDate, b.effectiveDate)
      )
      .map((t) => ({
        label: `${formatDateOnlyForDisplay(t.effectiveDate)}${
          t.endDate ? ` – ${formatDateOnlyForDisplay(t.endDate)}` : ""
        } · ${t.status}`,
        value: t.id,
      }));
  }, [termsInGroup]);

  /** Per-term delete only when the group has 2+ terms; a lone term = whole contract (use list "delete entire contract"). */
  const hasMultipleTermsInGroup = (termsInGroup?.length ?? 0) > 1;

  const [showDeleteTermDialog, setShowDeleteTermDialog] = useState(false);
  const [showAddTermDialog, setShowAddTermDialog] = useState(false);
  const [addTermEffectiveDate, setAddTermEffectiveDate] = useState<Date | null>(
    null
  );
  const [addTermBaseRateFileId, setAddTermBaseRateFileId] = useState<
    string | null
  >(null);
  const [addTermBaseRateMode, setAddTermBaseRateMode] = useState<
    "copy" | "newFile"
  >("copy");

  const addTermBaseFilePickOptions = useMemo(() => {
    if (!baseRateFiles?.data?.length) return [];
    const files = baseRateFiles.data.filter((f) => f.isCarveout !== true);
    return files.map((f) => ({
      label: `${f.displayName}${
        f.effectiveYear != null ? ` (${f.effectiveYear})` : ""
      }`,
      value: f.id,
    }));
  }, [baseRateFiles]);

  const addTermEffectiveDateDuplicatesExisting = useMemo(() => {
    if (!addTermEffectiveDate) return false;
    const iso = localCalendarDateToIsoDateString(addTermEffectiveDate);
    if (!iso) return false;
    return (
      termsInGroup?.some((t) => {
        const existing = normalizeToIsoDateOnly(t.effectiveDate);
        return existing !== undefined && existing === iso;
      }) ?? false
    );
  }, [addTermEffectiveDate, termsInGroup]);

  // Generate procedures dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [acknowledgeOverwrite, setAcknowledgeOverwrite] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<any>(null);

  useEffect(() => {
    setIsLoading(isLoading);
    return () => {
      setIsLoading(false);
    };
  }, [isLoading, setIsLoading]);

  useEffect(() => {
    if (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load contract details.",
      });
    }
  }, [error, toastElement]);

  // Auto-refresh when contract is importing
  useEffect(() => {
    if (contract?.status === "Importing") {
      const interval = setInterval(() => {
        refetch();
        refetchContractList();
      }, 5000);

      return () => clearInterval(interval);
    }
    return () => {};
  }, [contract?.status, refetch, refetchContractList]);

  if (!contract) {
    return (
      <div className="card">
        <div className="text-center p-4">
          {isLoading ? "Loading contract details..." : "Contract not found"}
        </div>
      </div>
    );
  }

  const contractsListBasePath = hospitalId
    ? `/hospitals/view/${hospitalId}/contracts`
    : `/payer-rules/contracts`;

  const navigateToTerm = (newId: string) => {
    navigate(`${contractsListBasePath}/${newId}`, {
      replace: true,
      state: location.state,
    });
  };

  const handleDeleteTermConfirm = async () => {
    if (!actualContractId || !contract) return;
    const termIdToDelete = actualContractId;
    try {
      await deleteContractTerm(termIdToDelete).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Term removed",
        detail: "This contract period was deleted.",
      });
      setShowDeleteTermDialog(false);
      const { data: freshList } = await refetchContractList();
      const row = (freshList ?? []).find(
        (r) =>
          r.providerId === contract.providerId && r.payerId === contract.payerId
      );
      let nextTermId: string | null = null;
      if (row) {
        // eslint-disable-next-line no-underscore-dangle
        const terms = row._termsInGroup;
        if (terms?.length) {
          const others = terms.filter((t) => t.id !== termIdToDelete);
          if (others.length > 0) {
            nextTermId = [...others].sort((a, b) =>
              compareDateOnlyStringsDesc(a.effectiveDate, b.effectiveDate)
            )[0].id;
          }
        } else if (row.id !== termIdToDelete) {
          nextTermId = row.id;
        }
      }
      if (nextTermId) {
        navigateToTerm(nextTermId);
      } else {
        navigate(contractsListBasePath);
      }
    } catch (err: any) {
      const status = err?.status ?? err?.originalStatus;
      const msg =
        status === 404
          ? "This term was already removed or could not be found."
          : err?.data?.messages?.[0] ||
            err?.data?.message ||
            "Could not delete this term.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: msg,
      });
    }
  };

  const handleAddTermSubmit = async () => {
    const iso = localCalendarDateToIsoDateString(addTermEffectiveDate);
    if (!iso) {
      toastElement?.show({
        severity: "error",
        summary: "Validation",
        detail: "Select an effective date for the new term.",
      });
      return;
    }
    const duplicatesExistingTerm = termsInGroup?.some((t) => {
      const existing = normalizeToIsoDateOnly(t.effectiveDate);
      return existing !== undefined && existing === iso;
    });
    if (duplicatesExistingTerm) {
      return;
    }
    try {
      const res = await createNextTerm({
        id: actualContractId!,
        effectiveDate: iso,
        ...(addTermBaseRateMode === "newFile" && addTermBaseRateFileId
          ? { newBaseRateFileId: addTermBaseRateFileId }
          : {}),
      }).unwrap();
      const newId = res.data?.id;
      await refetchContractList();
      toastElement?.show({
        severity: "success",
        summary: "Term created",
        detail: "The new contract term is active.",
      });
      setShowAddTermDialog(false);
      setAddTermEffectiveDate(null);
      setAddTermBaseRateFileId(null);
      setAddTermBaseRateMode("copy");
      if (newId) navigateToTerm(newId);
      else refetch();
    } catch (err: any) {
      const msg =
        err?.data?.messages?.[0] ||
        err?.data?.message ||
        "Could not create the next term.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: msg,
      });
    }
  };

  const statusSeverityMap = {
    Draft: "info",
    Active: "success",
    Inactive: "warning",
    Expired: "danger",
    Importing: "warning",
  } as const;

  const getButtonLabel = (status?: string, dryRunResult?: any) => {
    if (status === "Draft") {
      return dryRunResult ? "Activate Contract" : "Activate";
    }
    return dryRunResult ? "Generate" : "Generate";
  };

  const getRateTypeForDisplay = (calc: RateCalculation): RateType =>
    normalizeRateType(calc.rateType);

  const rateCalculationBodyTemplate = (rowData: RateCalculation) => {
    const baseRateFile = baseRateFiles?.data?.find(
      (f) => f.id === rowData.baseRateFileId
    );
    // CLE-2796 — say what the file declares, and flag when the contract holds far fewer rows
    // than that. "17,539 procedures" was printed here for four months while 1,350 existed.
    const declaredCount = baseRateFile?.metadata?.rowCount;
    const generatedCount = contract.generatedProcedureCount;
    const shortfall =
      declaredCount !== undefined &&
      generatedCount !== undefined &&
      contract.status === "Active" &&
      generatedCount < declaredCount * 0.95;
    const baseRateFileName = resolveRateCalculationBaseRateDisplayName(
      rowData,
      baseRateFiles?.data
    );
    const procedureText =
      declaredCount !== undefined
        ? ` (${declaredCount.toLocaleString()} rows in file)`
        : "";
    const rateType = getRateTypeForDisplay(rowData);

    return (
      <div className="text-900">
        {rateType === "unit"
          ? `$${(rowData.ratePercent / 100).toFixed(
              2
            )} per ${baseRateFileName}${procedureText}`
          : `${rowData.ratePercent}% of ${baseRateFileName}${procedureText}`}
        {shortfall && (
          <div className="text-orange-700 text-sm mt-1">
            Only {generatedCount.toLocaleString()} procedures were generated
            from this file. Re-upload the file and regenerate.
          </div>
        )}
      </div>
    );
  };

  const carveoutDetailTemplate = (carveout: {
    fileName: string;
    ratePercent?: number | null;
    rateType?: RateType;
    metadata?: { rowCount?: number };
  }) => {
    const rowCount = carveout.metadata?.rowCount;
    const rowsText =
      rowCount !== undefined ? ` (${rowCount.toLocaleString()} rows)` : "";
    const percent =
      carveout.ratePercent != null ? Number(carveout.ratePercent) : 100;
    const rateType = normalizeRateType(carveout.rateType);
    return (
      <div className="text-900">
        {rateType === "unit"
          ? `$${(percent / 100).toFixed(2)} per ${carveout.fileName}${rowsText}`
          : `${percent}% of ${carveout.fileName}${rowsText}`}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="mb-3">
        <Button
          label="Return to Contracts"
          icon="pi pi-arrow-left"
          className="p-button-text p-button-plain text-600"
          onClick={() => navigate("/payer-rules?tab=3")}
        />
      </div>

      {/* Outdated Procedures Warning */}
      {contract.proceduresOutdated && contract.status === "Active" && (
        <Message
          severity="warn"
          className="mb-4"
          text="The base rate files for this contract have been updated since the provider procedures were last generated. To ensure accurate pricing, please regenerate the procedures using the 'Re-generate Procedures' button."
        />
      )}

      <div className="flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="m-0 mb-2">
            {contract.provider?.name} - {contract.payer?.name}
          </h2>
          <div className="flex align-items-center gap-2">
            <Badge
              value={contract.status}
              severity={statusSeverityMap[contract.status]}
            />
            {contract.proceduresOutdated && contract.status === "Active" && (
              <span title="Base rates have been updated. Procedures need regeneration.">
                <Badge value="Outdated" severity="warning" className="ml-2" />
              </span>
            )}
            {contract.status === "Importing" && (
              <span className="text-warning ml-2">
                <i className="pi pi-spin pi-spinner mr-2" />
                Fee schedule is being imported...
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 align-items-center flex-wrap">
          {termSwitcherOptions.length > 1 && (
            <Dropdown
              value={actualContractId}
              options={termSwitcherOptions}
              onChange={(e) => navigateToTerm(e.value)}
              placeholder="Switch term"
              className="min-w-12rem"
            />
          )}
          {contract.status === "Active" && (
            <Button
              label="Add term"
              icon="pi pi-calendar-plus"
              className="p-button-outlined"
              onClick={() => {
                setAddTermEffectiveDate(null);
                setAddTermBaseRateFileId(null);
                setAddTermBaseRateMode("copy");
                setShowAddTermDialog(true);
              }}
            />
          )}
          {hasMultipleTermsInGroup && (
            <Button
              label="Delete this term"
              icon="pi pi-trash"
              className="p-button-outlined p-button-danger"
              disabled={contract.status === "Importing" || isDeletingTerm}
              onClick={() => setShowDeleteTermDialog(true)}
            />
          )}
          <Button
            label="Edit"
            icon="pi pi-pencil"
            className="p-button-outlined"
            onClick={() => {
              const editUrl = hospitalId
                ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}/edit`
                : `/payer-rules/contracts/${actualContractId}/edit`;
              navigate(editUrl);
            }}
          />
          <Button
            label="Preview"
            icon="pi pi-eye"
            className="p-button-outlined"
            onClick={() => {
              const previewUrl = hospitalId
                ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}/preview`
                : `/payer-rules/contracts/${actualContractId}/preview`;
              navigate(previewUrl);
            }}
          />
          <Button
            label="Export"
            icon="pi pi-download"
            className="p-button-outlined"
            onClick={async () => {
              try {
                setIsLoading(true);
                const result = await exportFeeSchedule({
                  contractId: actualContractId!,
                  params: {},
                }).unwrap();

                // Create a download link for the CSV
                const url = window.URL.createObjectURL(result);
                const a = document.createElement("a");
                a.href = url;
                a.download = `fee-schedule-${
                  contract.provider?.name || "provider"
                }-${contract.payer?.name || "payer"}-${
                  new Date().toISOString().split("T")[0]
                }.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                toastElement?.show({
                  severity: "success",
                  summary: "Export Complete",
                  detail: "Fee schedule CSV exported successfully.",
                });
              } catch (error) {
                toastElement?.show({
                  severity: "error",
                  summary: "Export Failed",
                  detail: "Failed to export fee schedule.",
                });
              } finally {
                setIsLoading(false);
              }
            }}
          />
          {contract.status === "Draft" && (
            <Button
              label="Activate Contract"
              icon="pi pi-check-circle"
              className="p-button-success"
              onClick={() => setShowGenerateDialog(true)}
            />
          )}
          {contract.status === "Active" && (
            <Button
              label="Re-generate Procedures"
              icon="pi pi-sync"
              className="p-button-outlined"
              onClick={() => setShowGenerateDialog(true)}
            />
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid mb-4">
        <div className="col-12 md:col-6">
          <Card title="Contract Details" className="h-full">
            <div className="grid">
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Provider
                </label>
                <div className="text-900">
                  {contract.provider?.name || "N/A"}
                </div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">Payer</label>
                <div className="text-900">{contract.payer?.name || "N/A"}</div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Effective Date
                </label>
                <div className="text-900">
                  {formatDateOnlyForDisplay(contract.effectiveDate)}
                </div>
              </div>
              {contract.endDate && (
                <div className="col-6">
                  <label className="block text-600 font-medium mb-1">
                    End Date
                  </label>
                  <div className="text-900">
                    {formatDateOnlyForDisplay(contract.endDate)}
                  </div>
                </div>
              )}
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Next Update Date
                </label>
                <div className="text-900">
                  {contract.nextUpdateDate
                    ? formatDateOnlyForDisplay(contract.nextUpdateDate)
                    : "Not set"}
                </div>
              </div>
              {/* CLE-2781 — what happens to the rates on that date. */}
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Update Term
                </label>
                <div className="text-900">
                  {formatUpdateTerm(
                    contract.updateTerm,
                    contract.updatePercent
                  ) ?? "Not set"}
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <div className="grid">
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Created
                </label>
                <div className="text-900 text-sm">
                  {new Date(contract.createdAt).toLocaleString()}
                </div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Last Updated
                </label>
                <div className="text-900 text-sm">
                  {new Date(contract.updatedAt).toLocaleString()}
                </div>
              </div>
              <div className="col-12">
                <label className="block text-600 font-medium mb-1">
                  Created By
                </label>
                <div className="text-900">
                  {contract.createdBy?.email || "System"}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* CLE-2788 — implant terms, above notes. */}
      {contract.implantTerms && (
        <Card title="Implant Terms" className="mb-4">
          <div className="text-900" style={{ whiteSpace: "pre-wrap" }}>
            {contract.implantTerms}
          </div>
        </Card>
      )}

      {/* Notes */}
      {contract.notes && (
        <Card title="Notes" className="mb-4">
          {/* PrimeFlex has no white-space-pre-wrap utility, so set it inline to
              keep the line breaks the user typed in the edit form. */}
          <div className="text-900" style={{ whiteSpace: "pre-wrap" }}>
            {contract.notes}
          </div>
        </Card>
      )}

      {/* Linked payers that reuse this contract's rates (CLE-2751) */}
      {contractCanLinkPayers(contract.payer?.type) &&
        contract.medicareAdvantagePlans &&
        contract.medicareAdvantagePlans.length > 0 && (
          <Card
            title={linkedPayerCopy(contract.payer?.type).title}
            className="mb-4"
          >
            <div className="flex flex-wrap gap-2">
              {contract.medicareAdvantagePlans.map((link: any) => (
                <Chip
                  key={link.medicareAdvantagePlanId}
                  label={
                    link.medicareAdvantagePlan?.name ||
                    `Payer ${link.medicareAdvantagePlanId}`
                  }
                />
              ))}
            </div>
          </Card>
        )}

      {/* Providers sharing this contract (CLE-2786). The contract's own provider is the
          primary and is shown first, matching how the link is stored. */}
      {contract.linkedProviders && contract.linkedProviders.length > 0 && (
        <Card title={LINKED_PROVIDERS_COPY.title} className="mb-4">
          <div className="flex flex-wrap gap-2">
            {sharedProviderNames(
              contract.provider?.name,
              contract.linkedProviders
            ).map((name, index) => (
              <Chip
                key={name}
                label={index === 0 ? `${name} (primary)` : name}
              />
            ))}
          </div>
          <small className="text-600 mt-2 block">
            All of these providers are priced from this one contract.
          </small>
        </Card>
      )}

      {/* Rate Calculations (base rate files + carve-outs) */}
      <Card title="Rate Calculations" className="mb-4">
        {(contract.rateCalculations && contract.rateCalculations.length > 0) ||
        (contract.carveOutFiles && contract.carveOutFiles.length > 0) ? (
          <DataTable
            value={[
              ...(contract.rateCalculations ?? []).map((calc, i) => ({
                key: calc.id ?? `rc-${i}`,
                kind: "rateCalc" as const,
                calc,
                carveout: undefined,
              })),
              ...(contract.carveOutFiles ?? []).map((c) => ({
                key: `co-${c.id}`,
                kind: "carveout" as const,
                calc: undefined,
                carveout: c,
              })),
            ]}
            dataKey="key"
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column
              header="Rate Details"
              body={(row) =>
                row.kind === "carveout"
                  ? carveoutDetailTemplate(row.carveout)
                  : rateCalculationBodyTemplate(row.calc)
              }
            />
            <Column
              header="Type"
              style={{ width: "130px" }}
              body={(row) =>
                row.kind === "carveout" ? (
                  <Tag value="Carve-out" severity="warning" />
                ) : (
                  <Tag value="Base rate" severity="info" />
                )
              }
            />
            <Column
              header="Actions"
              style={{ width: "100px" }}
              body={(row) =>
                row.kind === "carveout" ? (
                  <Button
                    icon="pi pi-download"
                    className="p-button-rounded p-button-text p-button-sm"
                    tooltip="Download"
                    onClick={async () => {
                      try {
                        setIsLoading(true);
                        // Create a download link using the S3 key
                        const apiUrl =
                          (window as any).config?.apiUrl ||
                          "http://localhost:3000";
                        const response = await fetch(
                          `${apiUrl}/contract-fee-schedules/${actualContractId}/carveouts/${row.carveout.id}/download`,
                          {
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                          }
                        );

                        if (!response.ok) {
                          throw new Error("Download failed");
                        }

                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = row.carveout.fileName;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        toastElement?.show({
                          severity: "error",
                          summary: "Download Failed",
                          detail: "Failed to download carveout file.",
                        });
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                  />
                ) : null
              }
            />
          </DataTable>
        ) : (
          <div className="text-center p-4 text-600">
            No rate calculations configured for this contract.
          </div>
        )}
      </Card>

      {/* Adjustment Rules — unified section that will replace the separate
          per-type ruleset cards below. */}
      <AdjustmentRules
        mpdrRulesets={contract.mpdrRulesets}
        caseRateRulesets={contract.caseRateRulesets}
        inpatientRulesets={contract.inpatientRulesets}
        explodingFeeScheduleRulesets={contract.explodingFeeScheduleRulesets}
        packageRulesets={contract.packageRulesets}
        hospitalId={rulesReadOnlyHospitalId}
        providerId={contract.providerId}
        payerId={contract.payerId}
        // CLE-2264 — enables "Copy rules from…"; hidden in hospital context.
        contractId={isSuperAdmin ? contract.id : undefined}
        onRuleAdded={() => refetch()}
        showProfessional={
          !!contract.providerId &&
          !!contract.payerId &&
          contract.provider?.specialty !== SpecialtyList.FACILITY &&
          contract.provider?.specialty !== SpecialtyList.ANESTHESIOLOGIST
        }
        // CLE-2541 — the complement of showProfessional over Provider.specialty. Without this a
        // facility contract shows no primary-selection rule at all, since the Professional row is
        // hidden for exactly this population.
        showInstitutional={
          !!contract.providerId &&
          !!contract.payerId &&
          contract.provider?.specialty === SpecialtyList.FACILITY
        }
        providerName={contract.provider?.name}
        payerName={contract.payer?.name}
        addFromDefaultOptions={
          !isSuperAdmin
            ? undefined
            : [
                {
                  label: "MPPR rule",
                  entityLabel: "MPPR rule",
                  useList: useGetAllDiscountRulesetsQuery,
                  useCopy: useCopyDiscountRulesetToContractMutation,
                },
                {
                  label: "Case rate rule",
                  entityLabel: "Case rate rule",
                  useList: useGetCaseRateRulesetsQuery,
                  useCopy: useCopyCaseRateToContractMutation,
                },
                {
                  label: "Inpatient ruleset",
                  entityLabel: "Inpatient ruleset",
                  useList: useGetInpatientRulesetsQuery,
                  useCopy: useCopyInpatientRulesetToContractMutation,
                },
                {
                  label: "Exploding fee schedule",
                  entityLabel: "Exploding fee schedule",
                  useList: useGetAllExplodingFeeSchedulesQuery,
                  useCopy: useCopyExplodingFeeScheduleToContractMutation,
                },
                {
                  label: "Package rule",
                  entityLabel: "Package rule",
                  useList: useGetAllPackageRulesetsQuery,
                  useCopy: useCopyPackageRulesetToContractMutation,
                },
                // CLE-2524 — the four types below always had copy-to-contract on the
                // backend but were unreachable from the UI. Unit Cap forced the issue:
                // it is typical-capable with no fallback resolver, so without a copy
                // button a Typical unit cap could be created but never used.
                {
                  label: "Professional modifier rule",
                  entityLabel: "Professional modifier rule",
                  useList: useGetAllModifierAdjustmentsQuery,
                  useCopy: useCopyModifierAdjustmentToContractMutation,
                },
                {
                  label: "Lesser-of charges rule",
                  entityLabel: "Lesser-of charges rule",
                  useList: useGetAllLesserOfChargesQuery,
                  useCopy: useCopyLesserOfChargesToContractMutation,
                },
                {
                  label: "Charge-based pricing rule",
                  entityLabel: "Charge-based pricing rule",
                  useList: useGetAllChargeBasedPricingQuery,
                  useCopy: useCopyChargeBasedPricingToContractMutation,
                },
                {
                  label: "Unit cap rule",
                  entityLabel: "Unit cap rule",
                  useList: useGetAllUnitCapQuery,
                  useCopy: useCopyUnitCapToContractMutation,
                },
              ]
        }
      />

      {/* Contract Attachments */}
      <Card title="Contract Attachments" className="mb-4">
        {contract.attachments && contract.attachments.length > 0 ? (
          <DataTable
            value={contract.attachments}
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column field="fileName" header="File Name" />
            <Column
              field="fileSize"
              header="Size"
              body={(rowData) => {
                const kb = rowData.fileSize / 1024;
                return kb >= 1024
                  ? `${(kb / 1024).toFixed(2)} MB`
                  : `${kb.toFixed(2)} KB`;
              }}
            />
            <Column
              header="Uploaded By"
              body={(rowData) => rowData.uploadedBy?.name || "-"}
            />
            <Column
              field="createdAt"
              header="Upload Date"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
            />
            <Column
              header="Actions"
              body={(rowData) => (
                <Button
                  icon="pi pi-download"
                  className="p-button-rounded p-button-text p-button-sm"
                  tooltip="Download"
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      const apiUrl =
                        (window as any).config?.apiUrl ||
                        "http://localhost:3000";
                      const response = await fetch(
                        `${apiUrl}/contract-fee-schedules/${actualContractId}/attachments/${rowData.id}/download`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        }
                      );

                      if (!response.ok) {
                        throw new Error("Download failed");
                      }

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = rowData.fileName;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      toastElement?.show({
                        severity: "error",
                        summary: "Download Failed",
                        detail: "Failed to download attachment file.",
                      });
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                />
              )}
            />
          </DataTable>
        ) : (
          <div className="text-center p-4 text-600">
            No files attached to this contract.
          </div>
        )}
      </Card>

      {/* Contract History */}
      <ContractHistory histories={contract.histories} />

      {/* Generate Procedures Dialog */}
      <Dialog
        header={
          contract?.status === "Draft"
            ? "Activate Contract"
            : "Generate Provider Procedures"
        }
        visible={showGenerateDialog}
        style={{ width: "600px" }}
        onHide={() => {
          setShowGenerateDialog(false);
          setDryRunResult(null);
          setAcknowledgeOverwrite(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setShowGenerateDialog(false);
                setDryRunResult(null);
                setAcknowledgeOverwrite(false);
              }}
              className="p-button-text"
            />
            <Button
              label={getButtonLabel(contract?.status, dryRunResult)}
              icon="pi pi-check"
              disabled={!acknowledgeOverwrite}
              onClick={async () => {
                try {
                  setIsLoading(true);

                  // If contract is Draft, activate it first
                  if (contract?.status === "Draft") {
                    await activateContract(actualContractId!).unwrap();
                  }

                  const result = await generateProcedures({
                    contractId: actualContractId!,
                    options: { overwriteExisting: true },
                  }).unwrap();

                  // Since the endpoint returns 202 Accepted for async processing
                  if (result.status === "processing") {
                    toastElement?.show({
                      severity: "info",
                      summary: "Import Started",
                      detail:
                        "The fee schedule import has started and will take a few minutes. The page will refresh automatically when complete.",
                      life: 10000,
                    });
                  } else {
                    // Legacy handling if backend still returns sync response
                    toastElement?.show({
                      severity: "success",
                      summary:
                        contract?.status === "Draft"
                          ? "Contract Activated"
                          : "Success",
                      detail:
                        contract?.status === "Draft"
                          ? `Contract activated and ${(
                              result.message || "procedures generated"
                            ).toLowerCase()}`
                          : result.message ||
                            "Procedures generated successfully",
                    });
                  }
                  setShowGenerateDialog(false);
                  setDryRunResult(null);
                  setAcknowledgeOverwrite(false);
                } catch (error: any) {
                  toastElement?.show({
                    severity: "error",
                    summary:
                      contract?.status === "Draft"
                        ? "Activation Failed"
                        : "Generation Failed",
                    detail: error.data?.message || "Failed to process request.",
                  });
                } finally {
                  setIsLoading(false);
                }
              }}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <Message
            severity={contract?.status === "Draft" ? "warn" : "info"}
            text={
              contract?.status === "Draft"
                ? `This will activate the contract and generate provider procedures from the calculated rates for ${contract.provider?.name} and ${contract.payer?.name}. Once activated, the contract will be used for estimate processing.`
                : `This will generate additional provider procedures from the contract's calculated rates for ${contract.provider?.name} and ${contract.payer?.name}.`
            }
            className="mb-4"
          />

          <div className="field-checkbox mb-4">
            <Checkbox
              inputId="acknowledgeOverwrite"
              checked={acknowledgeOverwrite}
              onChange={(e) => setAcknowledgeOverwrite(e.checked || false)}
            />
            <label htmlFor="acknowledgeOverwrite" className="ml-2">
              I acknowledge that this will overwrite any existing fee schedules
              for this provider and payer
            </label>
          </div>

          {dryRunResult && (
            <div className="mt-4">
              <h4>Preview Results</h4>
              <p>
                <strong>Total Procedures:</strong> {dryRunResult.procedureCount}
              </p>
              <p>
                <strong>Total Rate:</strong> $
                {dryRunResult.totalRate?.toFixed(2)}
              </p>

              {dryRunResult.procedures &&
                dryRunResult.procedures.length > 0 && (
                  <>
                    <h5 className="mt-3">Sample Procedures (First 10):</h5>
                    <DataTable
                      value={dryRunResult.procedures}
                      className="p-datatable-sm"
                      scrollable
                      scrollHeight="200px"
                    >
                      <Column field="cptCode" header="CPT Code" />
                      <Column field="description" header="Description" />
                      <Column
                        field="priceInCents"
                        header="Price"
                        body={(rowData) =>
                          `$${(rowData.priceInCents / 100).toFixed(2)}`
                        }
                      />
                    </DataTable>
                  </>
                )}
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        header="Delete this term?"
        visible={showDeleteTermDialog && hasMultipleTermsInGroup}
        style={{ width: "520px" }}
        onHide={() => {
          if (!isDeletingTerm) setShowDeleteTermDialog(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              className="p-button-text"
              disabled={isDeletingTerm}
              onClick={() => setShowDeleteTermDialog(false)}
            />
            <Button
              label="Delete term"
              className="p-button-danger"
              icon="pi pi-trash"
              loading={isDeletingTerm}
              disabled={isDeletingTerm}
              onClick={handleDeleteTermConfirm}
            />
          </div>
        }
      >
        <p className="text-900 mb-2">
          <strong>{contract.provider?.name}</strong>
          {" → "}
          <strong>{contract.payer?.name}</strong>
        </p>
        <p className="text-600 mb-3">
          Period: {formatDateOnlyForDisplay(contract.effectiveDate)}
          {contract.endDate
            ? ` – ${formatDateOnlyForDisplay(contract.endDate)}`
            : " (open-ended)"}
          . Status: {contract.status}.
        </p>
        <p className="text-600 mb-0">
          This removes contract-backed prices and linked rules for{" "}
          <strong>this period only</strong>. Claims and estimates for this date
          range may be affected. Other terms in the same contract are unchanged.
          This does not delete the entire multi-year contract unless this was
          the last term.
        </p>
      </Dialog>

      <Dialog
        header="Add contract term"
        visible={showAddTermDialog}
        style={{ width: "480px" }}
        onHide={() => {
          if (!isCreatingTerm) setShowAddTermDialog(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              className="p-button-text"
              disabled={isCreatingTerm}
              onClick={() => setShowAddTermDialog(false)}
            />
            <Button
              label="Create term"
              loading={isCreatingTerm}
              disabled={
                isCreatingTerm ||
                addTermEffectiveDateDuplicatesExisting ||
                (addTermBaseRateMode === "newFile" &&
                  (!addTermBaseRateFileId ||
                    addTermBaseFilePickOptions.length === 0))
              }
              onClick={handleAddTermSubmit}
            />
          </div>
        }
      >
        <div className="field">
          <label className="block mb-2 font-medium">Effective date *</label>
          <Calendar
            value={addTermEffectiveDate}
            onChange={(e) =>
              setAddTermEffectiveDate(
                normalizeCalendarDayToLocalNoon(e.value as Date | null)
              )
            }
            showIcon
            className={
              addTermEffectiveDateDuplicatesExisting
                ? "w-full p-invalid"
                : "w-full"
            }
            dateFormat="mm-dd-yy"
          />
          {addTermEffectiveDateDuplicatesExisting && (
            <small className="text-red-500 block mt-1">
              Another term in this contract already uses this effective date.
              Choose a different day.
            </small>
          )}
        </div>
        <div className="field">
          <span className="block mb-2 font-medium">Base rate lines *</span>
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center gap-2">
              <RadioButton
                inputId="addTermBrCopy"
                name="addTermBaseRate"
                checked={addTermBaseRateMode === "copy"}
                onChange={() => {
                  setAddTermBaseRateMode("copy");
                  setAddTermBaseRateFileId(null);
                }}
              />
              <label htmlFor="addTermBrCopy" className="mb-0 cursor-pointer">
                Copy from current term (same base rate file links)
              </label>
            </div>
            <div className="flex align-items-center gap-2">
              <RadioButton
                inputId="addTermBrNew"
                name="addTermBaseRate"
                checked={addTermBaseRateMode === "newFile"}
                onChange={() => setAddTermBaseRateMode("newFile")}
              />
              <label htmlFor="addTermBrNew" className="mb-0 cursor-pointer">
                Use a different base rate file
              </label>
            </div>
          </div>
        </div>
        {addTermBaseRateMode === "newFile" && (
          <div className="field">
            <label className="block mb-2 font-medium" htmlFor="addTermBrFile">
              Base rate file *
            </label>
            <Dropdown
              inputId="addTermBrFile"
              value={addTermBaseRateFileId}
              options={addTermBaseFilePickOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) =>
                setAddTermBaseRateFileId(
                  e.value != null && e.value !== "" ? String(e.value) : null
                )
              }
              placeholder="Select base rate file"
              className="w-full"
              filter
              showClear
            />
            {addTermBaseFilePickOptions.length === 0 && (
              <small className="text-red-500 block mt-1">
                No base rate files are available for this account. Upload one
                first or copy from the current term.
              </small>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}

export default ContractViewPage;
